import { isAxiosError } from 'axios'
import axiosInstance, { getAccessToken } from './axiosInstance'

type AxiosBaseQueryProps = {
  baseUrl?: string
}

type AxiosInstanceProps = {
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  data?: any,
  params?: any,
  headers?: any,
  responseType?: 'json' | 'blob' | 'text',
  includeAuth?: boolean
}

export interface IErrorResponse<T> {
  status: number | undefined;
  data: T | null;
}

const axiosBaseQuery =
  <T>(
    { baseUrl }: AxiosBaseQueryProps = {
      baseUrl: process.env.REACT_APP_BASE_URL ?? ''
    }
  ) =>
  async ({
    url,
    method,
    data,
    params,
    headers = {},
    responseType = 'json',
    includeAuth = true
  }: AxiosInstanceProps) => {
    try {
      // Axios is configured with a default `Content-Type: application/json`. When
      // a FormData payload comes through, that default makes axios JSON-stringify
      // the body. We unset it so axios falls back to its built-in FormData
      // handler that sets the right `multipart/form-data; boundary=...` header.
      const isFormData =
        typeof FormData !== 'undefined' && data instanceof FormData
      const finalHeaders: Record<string, any> = {
        ...headers,
        Authorization: includeAuth ? `Bearer ${getAccessToken()}` : undefined
      }
      if (isFormData) {
        // `null` makes axios drop the inherited default before serialising.
        finalHeaders['Content-Type'] = undefined
      }

      const result = await axiosInstance({
        url: baseUrl + url,
        method,
        data,
        params,
        headers: finalHeaders,
        responseType
      })

      return { data: result.data }
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        return {
          error: {
            status: error.response?.status,
            data: error.response?.data || null
          } as IErrorResponse<T>
        }
      }
      return Promise.reject(new Error('An unexpected error occurred.'))
    }
  }

export default axiosBaseQuery
